import type { ConnectedAccountNanoId } from '@composio/types'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import clsx from 'clsx'
import { Button } from 'design-system/ui/atoms/button'
import { cloneDeep } from 'lodash'
import {
  AlertTriangleIcon,
  CheckIcon,
  CopyIcon,
  ExternalLinkIcon,
  XIcon,
} from 'lucide-react'

import type { GetConnectedAccountsByNanoidResponse } from '~/generated/client'
import { QUERY_KEYS } from '~/constants/keys'
import { useApiKey } from '~/dataProcessor/hooks/user'
import { Switch } from '~/design-system/atom/switch'
import { ToolTipBox } from '~/design-system/atom/tooltip'
import { useToast } from '~/design-system/atom/use-toast'
import {
  patchConnectedAccountsByNanoIdStatus,
  postConnectedAccountsByNanoidRefresh,
} from '~/generated/client'
import { queryClient } from '~/queryClient'
import { IdDropDown } from '~/ui/components/base/idDropDown'
import { LoadingIcon } from '~/ui/components/base/loadingIcon'
import { CodeWInstructionModal } from '~/ui/containers/codeModal'
import { copyText } from '~/utils/dom'
import { formatUnderScoreString, upperCaseAll } from '~/utils/string'
import { formatDate } from '~/utils/time'
import { ProxyActionPlayground } from '../actions/proxyAction'
import { getConnectionStatus } from '../appScreen/modules/addConnectedAccount'
import { CodeButton } from '../connector'

export const removeKeyFromObject = (
  obj: Record<string, unknown>,
  key: string,
) => {
  const { [key]: _, ...rest } = obj
  return rest
}

export const Row = ({
  label,
  data,
  onCopy,
  leftIcon,
  extraData,
}: {
  label: string
  data: string | React.ReactNode
  onCopy?: () => void
  leftIcon?: () => React.ReactNode
  extraData?: string
}) => {
  const isCopyable = onCopy !== undefined
  const [isCopied, setIsCopied] = useState(false)

  return (
    <div
      className={clsx(
        'text-black-4000 border-grey-00 grid grid-cols-2 border-b-[1px] px-6 py-4 pb-4 text-[14px]',
      )}
    >
      <div className="flex items-center gap-2 break-words">{label}</div>
      <div className="flex items-center justify-end">
        {typeof leftIcon === 'function' ? leftIcon() : leftIcon}
        {isCopyable ? (
          <ToolTipBox
            side={'right'}
            onOpenChange={() => {
              setIsCopied(false)
            }}
            content={<div>{isCopied ? 'Copied' : 'Copy to clipboard'}</div>}
          >
            {extraData ? (
              <IdDropDown
                ids={[
                  {
                    key: 'v1/v2',
                    label: 'v1/v2 (uuid)',
                    id: extraData,
                  },
                ]}
              >
                <div
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsCopied(true)
                    onCopy()
                  }}
                  className="flex items-center justify-end gap-2 font-[500] text-black-400"
                >
                  {data}
                  <CopyIcon
                    height={15}
                    width={15}
                    className="min-h-[15px] min-w-[15px]"
                    strokeWidth={1.2}
                  />
                </div>
              </IdDropDown>
            ) : (
              <div
                onClick={(e) => {
                  e.stopPropagation()
                  setIsCopied(true)
                  onCopy()
                }}
                className="flex items-center justify-end gap-2 font-[500] text-black-400"
              >
                {data}
                <CopyIcon
                  height={15}
                  width={15}
                  className="min-h-[15px] min-w-[15px]"
                  strokeWidth={1.2}
                />
              </div>
            )}
          </ToolTipBox>
        ) : (
          <div className="flex justify-end font-[500] text-black-400">
            {data}
          </div>
        )}
      </div>
    </div>
  )
}

export const ConnectionInfoBlock = ({
  connectionInfo,
  connectionId,
}: {
  connectionInfo: GetConnectedAccountsByNanoidResponse | undefined
  connectionId: ConnectedAccountNanoId
}) => {
  const { toast } = useToast()
  const isActive = connectionInfo?.status === 'ACTIVE'

  const [isChecked, setIsChecked] = useState(false)

  useEffect(() => {
    setIsChecked(!connectionInfo?.is_disabled)
  }, [connectionInfo])

  const [showPlayground, setShowPlayground] = useState(false)
  const apiReqCounterRef = useRef(0)
  const { query } = useRouter()

  const connectionParams = connectionInfo?.params ?? {}

  const connectionStatusIcon = () => {
    switch (connectionInfo?.status) {
      case 'ACTIVE': {
        const icon = (
          <div className="mr-1 mt-[-4px] flex h-4 w-4 items-center justify-center rounded-full bg-green-600">
            <CheckIcon
              height={10}
              width={10}
              className="text-green-600"
              strokeWidth={2}
              color={'#fff'}
            />
          </div>
        )
        return connectionInfo?.status_reason ? (
          <ToolTipBox
            content={<div>{connectionInfo.status_reason}</div>}
            side={'bottom'}
            align="center"
          >
            {icon}
          </ToolTipBox>
        ) : (
          icon
        )
      }
      case 'EXPIRED': {
        const icon = (
          <div className="mr-1 flex h-5 w-5 items-center justify-center">
            <AlertTriangleIcon
              height={16}
              width={16}
              className="mt-[-4px] text-yellow-600"
              strokeWidth={2}
            />
          </div>
        )
        return (
          <ToolTipBox
            content={
              <div className="w-[300px]">{`${connectionInfo?.status_reason ?? 'Connection expired'}. Please re-authenticate to continue using this connection.`}</div>
            }
            side={'bottom'}
            align="center"
          >
            {icon}
          </ToolTipBox>
        )
      }
      case 'FAILED': {
        const icon = (
          <div className="mr-1 flex h-5 w-5 items-center justify-center">
            <div className="mt-[-4px] flex h-4 w-4 items-center justify-center rounded-full bg-red-600">
              <XIcon
                height={10}
                width={10}
                className="text-white"
                strokeWidth={2}
                color={'#fff'}
              />
            </div>
          </div>
        )
        return connectionInfo?.status_reason ? (
          <ToolTipBox
            content={
              <div className="w-[300px]">{connectionInfo.status_reason}</div>
            }
            side={'bottom'}
            align="center"
          >
            {icon}
          </ToolTipBox>
        ) : (
          icon
        )
      }
      case 'INITIATED': {
        return (
          <div className="mr-1 mt-[-4px] flex h-5 w-5 items-center justify-center">
            <LoadingIcon
              height={16}
              width={16}
              className="text-grey-400"
              strokeWidth={2}
            />
          </div>
        )
      }
      default:
        return null
    }
  }

  const handleReauthenticate = () => {
    // Call the reinitiate API endpoint
    postConnectedAccountsByNanoidRefresh({
      nanoid: connectionId,
    })
      .then((response) => {
        // Open the redirect URL in a new window
        const redirectUrl = response.redirect_url
        if (redirectUrl) {
          const windowObj = window.open(redirectUrl, '_blank')

          const interval = setInterval(() => {
            getConnectionStatus(connectionId).then((res) => {
              if (!['INITIATED', 'FAILED', 'EXPIRED'].includes(res.status)) {
                clearInterval(interval)
                windowObj?.close()
                window.location.reload()
                // Revalidate the connected accounts
                queryClient.invalidateQueries(
                  QUERY_KEYS.getConnectionsQueryKey(query),
                )
              }
            })

            apiReqCounterRef.current += 1

            if (apiReqCounterRef.current > 60) {
              // setStateInfo({ index: 1, data: { status: "failed" } });
              clearInterval(interval)
            }
          }, 2000)
        }
      })
      .catch(() => {
        toast({
          title: 'Error',
          description: 'Failed to reinitiate connection',
          variant: 'destructive',
        })
      })
  }

  if (!connectionInfo) return null

  return (
    <>
      {showPlayground && (
        <ProxyActionPlayground
          setOpen={setShowPlayground}
          connectionInfo={connectionInfo}
        />
      )}
      <ShowConnectionCode connectionId={connectionId as string} />

      <div
        className={clsx(
          ' my-6 min-w-[540px] max-w-[720px] rounded-[12px] border-[1px] border-grey-200 bg-[#fff]  text-[13px]',
        )}
      >
        {}
        {connectionInfo?.status === 'EXPIRED' &&
          (connectionParams as unknown as { redirectUrl: string })
            .redirectUrl && (
            <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
              <div className="flex items-center gap-2">
                <AlertTriangleIcon
                  height={16}
                  width={16}
                  className="text-yellow-600"
                  strokeWidth={2}
                />
                <div className="font-medium text-yellow-800">
                  Connection needs re-authorization
                </div>
              </div>
              <p className="mt-2 text-sm text-yellow-700">
                This connection has expired and requires re-authorization.
                Please re-authenticate to continue using the integration's
                features and ensure uninterrupted service.
              </p>
              <div className="mt-4 flex justify-start">
                <Button
                  onClick={handleReauthenticate}
                  size="32"
                  variant="secondary"
                  uplifted={false}
                >
                  Re-authenticate
                </Button>
              </div>
            </div>
          )}
        {/* <div className="grid grid-cols-2 border-b-[1px] border-grey-200 px-6  py-3 pb-[7px] text-[13px] font-[400] text-black-400">
                    <div>Label</div>
                    <div className="flex justify-end">Data</div>
                </div> */}
        <Row
          label="Last Updated At"
          data={formatDate(connectionInfo.updated_at, 'full')}
        />

        <Row
          label="App name"
          data={upperCaseAll(connectionInfo?.toolkit?.slug)}
          onCopy={() => {
            copyText(upperCaseAll(connectionInfo?.toolkit?.slug)!)
          }}
        />
        <Row
          label="Connection Id"
          data={connectionId as string}
          extraData={connectionInfo?.deprecated?.uuid}
          onCopy={() => {
            copyText(connectionId as string)
          }}
        />
        <Row
          label="Entity"
          data={connectionInfo.user_id}
          onCopy={() => {
            copyText(connectionInfo.user_id)
          }}
        />
        <Row
          label="Integration Id"
          data={
            <Link
              href={`/connector/${connectionInfo?.auth_config?.id}`}
              target="_blank"
              className="flex items-center gap-[4px] text-purple-900 underline"
            >
              {connectionInfo?.auth_config?.id}
              <ExternalLinkIcon height={12} width={12} strokeWidth={1.4} />
            </Link>
          }
        />
        <Row
          label="Status"
          data={connectionInfo?.status}
          leftIcon={connectionStatusIcon}
        />
        {isActive && (
          <Row
            label="Perform actions"
            data={
              <Button
                size="32"
                variant="primary"
                onClick={() => {
                  setShowPlayground(true)
                }}
                className="ml-2 max-w-[120px]"
                uplifted={false}
              >
                Execute actions
              </Button>
            }
          />
        )}
        <Row
          label="Enabled"
          data={
            <Switch
              checked={isChecked}
              id="enabled-mode"
              onCheckedChange={(e) => {
                setIsChecked(e)
                patchConnectedAccountsByNanoIdStatus({
                  nanoId: connectionId,
                  requestBody: {
                    enabled: e,
                  },
                })
                  .then(() => {
                    toast({
                      title: 'Status updated',
                      description: `Connector ${e ? 'enabled' : 'disabled'}`,
                    })
                  })
                  .catch(() => {
                    setIsChecked(!e)
                    toast({
                      title: 'Error',
                      description: `Error in updating status`,
                      variant: 'destructive',
                    })
                  })
              }}
            />
          }
        />
        {/* <Row label="Delete" data={<DeleteConnectionButton connectionId={connectionId as string} />} /> */}
        {Object.keys(connectionParams).map((key) => {
          const value = connectionParams[key]
          if (!value) return null
          const isHidden =
            value === '*'.repeat((value as string).length) ||
            (typeof value === 'object' &&
              value &&
              Object.keys(value).length === 0)
          if (isHidden) {
            return null
          }

          const valueToCopy =
            typeof value === 'string'
              ? value
              : !!value
                ? JSON.stringify(
                    removeKeyFromObject(
                      value as Record<string, unknown>,
                      'x-request-id',
                    ),
                  )
                : ''

          const fomattedValue =
            valueToCopy?.length > 50
              ? valueToCopy.slice(0, 50) + '...'
              : valueToCopy
          return (
            <Row
              key={key}
              label={formatUnderScoreString(key)}
              data={
                <div className="flex h-[20px] overflow-hidden whitespace-nowrap text-[13px] font-[400] tracking-[.35px]">
                  {fomattedValue}
                </div>
              }
              onCopy={() => {
                copyText(valueToCopy)
              }}
            />
          )
        })}
      </div>
    </>
  )
}

export const sampleCodeBlockData = {
  content: {
    python: [
      {
        type: 'blockWizard',
        content: {
          heading: 'Code for creating a connection for a user',
          blocks: [
            {
              heading: 'Get integration',
              text: `from composio import ComposioToolSet, App
# Example usage
toolset = ComposioToolSet(api_key={{api_key}})
connection = toolset.get_connected_account({{connection_id}})
print(connection)`,
              language: 'python',
            },
          ],
        },
      },
    ],
    javascript: [
      {
        type: 'blockWizard',
        content: {
          heading: 'Get integration',
          blocks: [
            {
              heading: 'Get integration',
              text: `import {Composio} from "composio-core";
// get composio client
const composio = new Composio({apiKey: {{api_key}}});

const connection = await composio.connectedAccounts.get({
    connectedAccountId: {{connection_id}}
})
`,
              language: 'javascript',
            },
          ],
        },
      },
    ],
  },
}

const ShowConnectionCode = ({ connectionId }: { connectionId: string }) => {
  const [showCode, setShowCode] = useState(false)

  const apiKey = useApiKey()

  const transformedData = useMemo(() => {
    // transform data of sampleCodeBlockData
    const data = cloneDeep(sampleCodeBlockData)

    for (const language of Object.keys(data.content)) {
      for (const languageItem of data.content[
        language as keyof typeof data.content
      ]) {
        //replace blocks with api key and integration id
        const { blocks } = languageItem.content

        if (!blocks) continue

        for (const block of blocks) {
          block.text = block.text
            .replace('{{api_key}}', `"${apiKey}"`)
            .replace('{{connection_id}}', `"${connectionId}"`)
        }
      }
    }

    return data
  }, [apiKey, connectionId])

  return (
    <div>
      <div className="mb-3 mt-3 flex min-w-[540px] max-w-[720px] items-center justify-between">
        <div className="text-black-4000 text-[14px] font-[500]">
          Get connection details using code
        </div>
        <CodeButton setShowCode={setShowCode} />
      </div>
      {showCode && (
        <CodeWInstructionModal
          title="Get connection code"
          data={transformedData}
          setOpen={() => setShowCode(false)}
        />
      )}
    </div>
  )
}
